import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Per-project design-step editor (project settings page).
//
// Shows the project's design steps. Until the owner/consultant clicks
// "Customize for this project" the steps are the company defaults shown
// read-only. After customizing they become editable: add / rename / remove
// (blocked when a step has files) / drag-to-reorder. All changes are saved
// over AJAX with no full-page reload.
export default class extends Controller {
  static targets = [
    "list", "customizeBanner", "addButton",
    "createModal", "createName",
    "editModal", "editId", "editName",
    "deleteModal", "deleteId", "deleteName"
  ]

  static values = {
    indexUrl: String,
    customizeUrl: String,
    createUrl: String,
    reorderUrl: String,
    stepUrlTemplate: String // e.g. /projects/35/design_steps/__ID__
  }

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.content
    this.customized = false
    this.steps = []
    this._sortable = null
    this._load()
  }

  disconnect() {
    this._destroySortable()
  }

  // ── Data ──────────────────────────────────────────────────────────

  async _load() {
    try {
      const resp = await fetch(this.indexUrlValue, { headers: this._headers() })
      const data = await resp.json()
      this.customized = data.customized
      this.steps = data.steps || []
      this._render()
    } catch {
      this.listTarget.innerHTML = `<p class="text-[13px] text-red-600 px-1 py-2">Couldn't load steps.</p>`
    }
  }

  async customize() {
    try {
      const resp = await fetch(this.customizeUrlValue, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      this.customized = data.customized
      this.steps = data.steps || []
      this._render()
      this._toast("Steps are now customized for this project")
    } catch {
      this._toast("Network error", "error")
    }
  }

  async submitCreate() {
    const name = this.createNameTarget.value.trim()
    if (!name) return
    try {
      const resp = await fetch(this.createUrlValue, {
        method: "POST", headers: this._headers(), body: JSON.stringify({ name })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.step) {
        this.steps.push(data.step)
        this.closeCreateModal()
        this._render()
        this._toast("Step added")
      } else {
        this._toast(data.error || "Couldn't add step", "error")
      }
    } catch {
      this._toast("Network error", "error")
    }
  }

  async submitRename() {
    const id = this.editIdTarget.value
    const name = this.editNameTarget.value.trim()
    if (!id || !name) return
    try {
      const resp = await fetch(this._stepUrl(id), {
        method: "PATCH", headers: this._headers(), body: JSON.stringify({ name })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.step) {
        const s = this.steps.find(x => String(x.id) === String(id))
        if (s) s.name = data.step.name
        this.closeEditModal()
        this._render()
        this._toast("Step renamed")
      } else {
        this._toast(data.error || "Couldn't rename step", "error")
      }
    } catch {
      this._toast("Network error", "error")
    }
  }

  async submitDelete() {
    const id = this.deleteIdTarget.value
    if (!id) return
    try {
      const resp = await fetch(this._stepUrl(id), { method: "DELETE", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.steps = this.steps.filter(x => String(x.id) !== String(id))
        this.closeDeleteModal()
        this._render()
        this._toast("Step removed")
      } else {
        this.closeDeleteModal()
        this._toast(data.error || "Couldn't remove step", "error")
      }
    } catch {
      this._toast("Network error", "error")
    }
  }

  async _persistOrder() {
    const ids = Array.from(this.listTarget.querySelectorAll("[data-step-id]"))
      .map(el => el.dataset.stepId)
    try {
      const resp = await fetch(this.reorderUrlValue, {
        method: "PATCH", headers: this._headers(), body: JSON.stringify({ ordered_ids: ids })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok) {
        this.steps = data.steps || this.steps
        this._toast("Order saved")
      } else {
        this._toast("Couldn't save order", "error")
        this._render()
      }
    } catch {
      this._toast("Network error", "error")
      this._render()
    }
  }

  // ── Rendering ─────────────────────────────────────────────────────

  _render() {
    this._destroySortable()

    if (this.hasCustomizeBannerTarget) {
      this.customizeBannerTarget.classList.toggle("hidden", this.customized)
    }
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.toggle("hidden", !this.customized)
    }

    if (!this.steps.length) {
      this.listTarget.innerHTML = `<p class="text-[13px] text-text-muted px-1 py-2">No design steps yet.</p>`
      return
    }

    this.listTarget.innerHTML = this.steps.map((s, i) => this._rowHtml(s, i)).join("")

    if (this.customized) this._initSortable()
  }

  _rowHtml(step, index) {
    const handle = this.customized
      ? `<span class="drag-handle cursor-grab text-gray-400 hover:text-gray-600 shrink-0" title="Drag to reorder">
           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5"/></svg>
         </span>`
      : ""

    const fileBadge = step.has_files
      ? `<span class="ml-2 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">Has files</span>`
      : ""

    let actions = ""
    if (this.customized) {
      const removeBtn = step.has_files
        ? `<button type="button" disabled class="w-full text-left px-3 py-1.5 text-sm text-gray-300 cursor-not-allowed" title="This step has files and can't be removed.">Remove</button>`
        : `<button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer" data-action="click->project-design-steps#openDeleteModal" data-id="${step.id}" data-name="${this._esc(step.name)}">Remove</button>`
      actions = `
        <div class="relative shrink-0" data-controller="dropdown">
          <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer" data-action="click->dropdown#toggle">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
          </button>
          <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-32" data-dropdown-target="menu">
            <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer" data-action="click->project-design-steps#openEditModal" data-id="${step.id}" data-name="${this._esc(step.name)}">Rename</button>
            ${removeBtn}
          </div>
        </div>`
    }

    return `<div class="step-row flex items-center gap-2 px-2 py-2 border border-gray-200 rounded-lg bg-white" data-step-id="${step.id}">
      ${handle}
      <span class="flex items-center justify-center text-[11px] font-semibold rounded-full bg-gray-100 text-gray-600 shrink-0" style="width:20px;height:20px;">${index + 1}</span>
      <span class="flex-1 text-[13px] text-gray-900 font-medium truncate">${this._esc(step.name)}</span>
      ${fileBadge}
      ${actions}
    </div>`
  }

  _initSortable() {
    this._sortable = Sortable.create(this.listTarget, {
      animation: 150,
      handle: ".drag-handle",
      draggable: ".step-row",
      ghostClass: "opacity-30",
      onEnd: () => this._persistOrder()
    })
  }

  _destroySortable() {
    if (this._sortable) { this._sortable.destroy(); this._sortable = null }
  }

  // ── Modals ────────────────────────────────────────────────────────

  openCreateModal() {
    this.createNameTarget.value = ""
    this.createModalTarget.classList.remove("hidden")
    this.createNameTarget.focus()
  }
  closeCreateModal() { this.createModalTarget.classList.add("hidden") }

  openEditModal(event) {
    this.editIdTarget.value = event.currentTarget.dataset.id
    this.editNameTarget.value = event.currentTarget.dataset.name
    this.editModalTarget.classList.remove("hidden")
    this.editNameTarget.focus()
  }
  closeEditModal() { this.editModalTarget.classList.add("hidden") }

  openDeleteModal(event) {
    this.deleteIdTarget.value = event.currentTarget.dataset.id
    if (this.hasDeleteNameTarget) this.deleteNameTarget.textContent = event.currentTarget.dataset.name
    this.deleteModalTarget.classList.remove("hidden")
  }
  closeDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) event.currentTarget.classList.add("hidden")
  }
  stopPropagation(event) { event.stopPropagation() }

  submitCreateOnEnter(e) { if (e.key === "Enter") { e.preventDefault(); this.submitCreate() } }
  submitRenameOnEnter(e) { if (e.key === "Enter") { e.preventDefault(); this.submitRename() } }

  // ── Helpers ───────────────────────────────────────────────────────

  _stepUrl(id) { return this.stepUrlTemplateValue.replace("__ID__", id) }

  _headers() {
    return { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, "Accept": "application/json" }
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;")
  }

  _toast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
